import { api, Alert } from '../api';
import { eventTypeLabel, eventTypeIcon } from '../main';
import { severityBadge, timeAgo, tabButtons, setupTabs } from '@demo/ui';
import { updateNavBadge } from '../notifications';

export async function renderAlerts(container: HTMLElement) {
  const allAlerts = await api.getAlerts();
  let tab: 'active' | 'resolved' = 'active';

  function renderList() {
    const filtered = allAlerts.filter(a => tab === 'active' ? !a.resolved : a.resolved);
    const list = container.querySelector('#alerts-list')!;

    container.querySelectorAll('[data-tab]').forEach(b => {
      const isActive = (b as HTMLElement).dataset.tab === tab;
      b.className = `px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-th-surface text-th-text-3 hover:text-th-text hover:bg-th-surface-hover'}`;
    });

    if (filtered.length === 0) {
      list.innerHTML = `<div class="text-center py-12 text-th-text-3">No ${tab} alerts</div>`;
      return;
    }

    list.innerHTML = filtered.map(a => `
      <div class="alert-card group flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-all cursor-pointer hover:bg-th-surface ${borderBySeverity(a.severity)} ${a.resolved ? 'opacity-60' : ''}" data-shipment="${a.shipment_id}">
        <div class="shrink-0 mt-0.5">${eventTypeIcon(a.event_type)}</div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1.5 flex-wrap">
            <span class="font-medium text-sm sm:text-base">${eventTypeLabel(a.event_type)}</span>
            ${severityBadge(a.severity)}
            ${a.resolved ? '<span class="text-xs text-green-500 font-medium">Resolved</span>' : ''}
          </div>
          <p class="text-xs sm:text-sm text-th-text-2 mb-2">${a.description}</p>
          <div class="flex items-center gap-2 sm:gap-4 text-xs text-th-text-3 flex-wrap">
            <span>${a.vehicle_name || a.vehicle_id}</span>
            <span>→ ${a.shipment_destination || a.shipment_id}</span>
            <span>${timeAgo(a.created_at)}</span>
          </div>
        </div>
        <div class="shrink-0 flex items-center gap-2">
          ${!a.resolved ? `<button class="resolve-btn sm:opacity-0 sm:group-hover:opacity-100 text-xs px-2.5 sm:px-3 py-1.5 bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/30 transition-all" data-alert-id="${a.id}">Resolve</button>` : ''}
          <svg class="w-4 h-4 text-th-text-4 group-hover:text-th-text-3 transition-colors hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
        </div>
      </div>
    `).join('');

    list.querySelectorAll('.alert-card').forEach(card => {
      card.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.resolve-btn')) return;
        const shipmentId = (card as HTMLElement).dataset.shipment;
        if (shipmentId) window.location.hash = `#/shipments/${shipmentId}`;
      });
    });

    list.querySelectorAll('.resolve-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const alertId = parseInt((btn as HTMLElement).dataset.alertId!);
        await api.resolveAlert(alertId);
        const alert = allAlerts.find(a => a.id === alertId);
        if (alert) {
          alert.resolved = true;
          alert.resolved_at = new Date().toISOString();
        }
        renderList();
        updateNavBadge();
      });
    });
  }

  const activeCount = allAlerts.filter(a => !a.resolved).length;
  const resolvedCount = allAlerts.filter(a => a.resolved).length;

  container.innerHTML = `
    <div class="h-full overflow-y-auto page-enter">
      <div class="p-4 sm:p-8 max-w-4xl">
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <h1 class="text-xl sm:text-2xl font-bold tracking-tight">Alerts</h1>
            <p class="text-sm text-th-text-3 mt-1">${activeCount} active, ${resolvedCount} resolved</p>
          </div>
          ${activeCount > 0 ? `<div class="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 self-start">
            <div class="w-2 h-2 rounded-full bg-red-400 animate-pulse"></div>
            <span class="text-xs font-medium text-red-400">${activeCount} requiring attention</span>
          </div>` : ''}
        </div>

        ${tabButtons([
          { id: 'active', label: `Active (${activeCount})` },
          { id: 'resolved', label: `Resolved (${resolvedCount})` },
        ], 'active')}

        <div class="space-y-3" id="alerts-list"></div>
      </div>
    </div>
  `;

  renderList();

  setupTabs(container, (tabId) => {
    tab = tabId as 'active' | 'resolved';
    renderList();
  });
}

function borderBySeverity(severity: string): string {
  const borders: Record<string, string> = {
    low: 'border-th-border-2 bg-th-surface',
    medium: 'border-amber-500/20 bg-amber-500/5',
    high: 'border-orange-500/20 bg-orange-500/5',
    critical: 'border-red-500/20 bg-red-500/5',
  };
  return borders[severity] || borders.low;
}
